import { Box, styled, Typography } from '@mui/material';
import type { Column } from 'react-table';
import {
  BoxAction,
  BoxIcon,
  Cell,
  DateTimeCell,
  Header,
  JobNameCell,
} from 'shared/components/planner/common/utils';
import { IconIsolationMode } from 'assets/img';
import { TFunctionNonStrict } from 'i18next';
import {
  PartnerShiftPlanningCellProp,
  PartnerShiftPlanningColumnDefinition,
} from '../shift-planning-table.types';
import { getNumberOfSeats } from '../../utils/get-number-of-seats';

const getShiftPlanningColumnDefinition: (
  t: TFunctionNonStrict<'partner-screen-specific', undefined>
) => Column<PartnerShiftPlanningColumnDefinition>[] = (t) => {
  const jobName = {
    Header: () => (
      <Header>
        {t('planning.shifts.table_headers.job_title', { ns: 'partner-screen-specific' })}
      </Header>
    ),
    accessor: 'jobName',
    Cell: JobNameCell,
    width: '200px',
  } as Column<PartnerShiftPlanningColumnDefinition>;
  const companyName = {
    Header: () => (
      <Header>
        {t('planning.shifts.table_headers.company', { ns: 'partner-screen-specific' })}
      </Header>
    ),
    accessor: 'companyName',
    Cell: Cell,
    width: '200px',
  } as Column<PartnerShiftPlanningColumnDefinition>;
  const departmentName = {
    Header: () => (
      <Header>
        {t('planning.shifts.table_headers.department', { ns: 'partner-screen-specific' })}
      </Header>
    ),
    accessor: 'departmentName',
    Cell: Cell,
  } as Column<PartnerShiftPlanningColumnDefinition>;
  const dateTime = {
    Header: () => (
      <Header>
        {t('planning.shifts.table_headers.date_time', { ns: 'partner-screen-specific' })}
      </Header>
    ),
    accessor: 'dateTime',
    Cell: DateTimeCell,
    width: '230px',
  } as Column<PartnerShiftPlanningColumnDefinition>;
  const state = {
    Header: () => (
      <Header>
        {t('planning.shifts.table_headers.max_claims', { ns: 'partner-screen-specific' })}
      </Header>
    ),
    accessor: 'state',
    Cell: PartnerStateCell,
    width: '65px',
  } as Column<PartnerShiftPlanningColumnDefinition>;
  const action = {
    Header: () => (
      <Header>
        {t('planning.shifts.table_headers.action', { ns: 'partner-screen-specific' })}
      </Header>
    ),
    accessor: 'action',
    Cell: ActionCell,
    width: '65px',
  } as Column<PartnerShiftPlanningColumnDefinition>;

  return [jobName, companyName, departmentName, dateTime, state, action];
};

const BoxState = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
}));
const BoxStateNumbers = styled(Box)(({ theme }) => ({
  display: 'flex',
  minWidth: theme.spacing(4),
}));

const PartnerStateCell = (props: PartnerShiftPlanningCellProp<'state'>): JSX.Element => {
  const numberOfSeats = getNumberOfSeats(props.value);
  return (
    <BoxState>
      <BoxStateNumbers>
        <Typography variant="body1" data-testid="open-seats">
          {numberOfSeats}
        </Typography>
      </BoxStateNumbers>
      {props.children}
    </BoxState>
  );
};

const ActionCell = (cell: PartnerShiftPlanningCellProp<'action'>) => {
  return (
    <BoxAction data-testid={`planner-table-action-${cell.value}`}>
      <BoxIcon>
        <IconIsolationMode
          onClick={() => {
            cell.openShiftDetail(cell.value);
          }}
        />
      </BoxIcon>
    </BoxAction>
  );
};

export { getShiftPlanningColumnDefinition };
